import React from "react";
import CodeBlock from "../components/CodeBlock";

const AdvancedTutorial = () => (
  <section id="advanced" className="developer-section">
    <h2>Advanced Tutorial</h2>
    <p>
      This section covers streaming results, graph expansion, evidence chains,
      molecular search, and LLM chat integration.
    </p>

    <h3>Streaming Search with SSE</h3>
    <p>
      For large searches, enable Server-Sent Events (SSE) streaming to receive progressive
      updates instead of waiting for the full result. Set <code>stream: true</code> in your
      search request to get a <code>stream_id</code> back immediately:
    </p>
    <CodeBlock
      title="Initiate a streaming search"
      examples={{
        python: `import json, requests
from sseclient import SSEClient  # use the bundled sseclient.py

HEADERS = {"x-api-key": token, "content-type": "application/json"}

# 1. Start streaming search (stream=true as query param)
resp = requests.post("/api/search", params={"stream": True},
                     json={"ids": ["unichem:161671"]}, headers=HEADERS)
stream_id = resp.json()["stream_id"]
stream_url = f"/api/stream/{stream_id}"

# 2. Iterate SSE events — SSEClient handles reconnection automatically
sse_resp = requests.get(stream_url,
                        headers={**HEADERS, "Accept": "text/event-stream"},
                        stream=True)
for event in SSEClient(sse_resp, events=["*"], reconnect_url=stream_url).events():
    if event.event == "search_update":
        data = json.loads(event.data)
        print(f"Progress: {data.get('progress', 0)}% - {data.get('info', '')}")
    elif event.event == "search_results":
        results = json.loads(event.data)
        print(f"Results received! Categories: {list(results['totals'].keys())}")
        break
    elif event.event == "search_error":
        print(f"Error: {event.data}")
        break`,
        javascript: `// 1. Start streaming search (stream=true as query param)
const resp = await fetch("/api/search?stream=true", {
  method: "POST",
  headers: { "x-api-key": token, "Content-Type": "application/json" },
  body: JSON.stringify({ ids: ["unichem:161671"] })
});
const { stream_id } = await resp.json();

// 2. Connect to SSE stream (token as query param for EventSource)
const es = new EventSource(\`/api/stream/\${stream_id}?token=\${token}\`);

es.addEventListener("search_update", (e) => {
  const data = JSON.parse(e.data);
  console.log(\`Progress: \${data.progress}% - \${data.info}\`);
});

es.addEventListener("search_results", (e) => {
  const results = JSON.parse(e.data);
  console.log("Results:", Object.keys(results.totals));
  es.close();
});

es.addEventListener("search_error", (e) => {
  console.error("Error:", e.data);
  es.close();
});`,
        curl: `# 1. Start streaming search
STREAM_ID=$(curl -s "/api/search?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"ids": ["unichem:161671"]}' | jq -r '.stream_id')

# 2. Connect to SSE stream
curl -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"`,
      }}
    />
    <p>
      See the <a href="#sse-streaming">SSE Streaming</a> section for the complete event type
      reference and connection management details.
    </p>

    <h3>Graph Expansion &amp; Similarity Parameters</h3>
    <p>
      Control how the knowledge graph is traversed and how results are scored:
    </p>
    <table className="dev-api-params">
      <thead>
        <tr><th>Parameter</th><th>Type</th><th>Default</th><th>Description</th></tr>
      </thead>
      <tbody>
        <tr>
          <td><code>sim_type</code></td>
          <td>string</td>
          <td>"sim"</td>
          <td>Similarity algorithm: <code>sim</code>, <code>chem</code>, <code>ecfp2</code>–<code>ecfp8</code>, <code>maccs</code>, <code>pubchem</code>, <code>default</code>.</td>
        </tr>
        <tr>
          <td><code>sim_threshold</code></td>
          <td>float</td>
          <td>0.75</td>
          <td>Minimum similarity score (0–1). Lower values return more, weaker connections.</td>
        </tr>
        <tr>
          <td><code>sim_limit</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Maximum number of similar items to include in expansion.</td>
        </tr>
        <tr>
          <td><code>sim_method</code></td>
          <td>string</td>
          <td>"tanimoto"</td>
          <td>Distance measure for chemical similarity: <code>tanimoto</code>, <code>tversky</code>, <code>euclid-sub</code>.</td>
        </tr>
        <tr>
          <td><code>sim_substructure</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Use substructure similarity search instead of full-molecule.</td>
        </tr>
        <tr>
          <td><code>limit_categories</code></td>
          <td>string[]</td>
          <td>&mdash;</td>
          <td>Restrict processing to these result category names.</td>
        </tr>
        <tr>
          <td><code>xf</code></td>
          <td>string</td>
          <td>&mdash;</td>
          <td>Expansion filter expression.</td>
        </tr>
        <tr>
          <td><code>min_expansion</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Minimum entities to find in the first expansion (overrides sim threshold).</td>
        </tr>
        <tr>
          <td><code>find_related</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Include the seed category in results (pseudo-similarity search).</td>
        </tr>
        <tr>
          <td><code>indirect</code></td>
          <td>boolean</td>
          <td>false</td>
          <td>Experimental: include indirect connections in results.</td>
        </tr>
        <tr>
          <td><code>density_expansion</code></td>
          <td>boolean</td>
          <td>true</td>
          <td>Density-aware expansion for geneset/compound set comparisons.</td>
        </tr>
        <tr>
          <td><code>neighborhood</code></td>
          <td>string</td>
          <td>&mdash;</td>
          <td>Filter results to a specific community ID in the graph.</td>
        </tr>
        <tr>
          <td><code>imap</code></td>
          <td>integer (0&ndash;6)</td>
          <td>&mdash;</td>
          <td>Graph configuration index to use for expansion.</td>
        </tr>
        <tr>
          <td><code>direct_connection_id</code></td>
          <td>string</td>
          <td>&mdash;</td>
          <td>Filter results to entities with a direct connection to this entity ID.</td>
        </tr>
        <tr>
          <td><code>datasets_filter</code></td>
          <td>string[]</td>
          <td>&mdash;</td>
          <td>Restrict results to these dataset names.</td>
        </tr>
        <tr>
          <td><code>page_size</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Number of results per page.</td>
        </tr>
        <tr>
          <td><code>limit</code></td>
          <td>integer</td>
          <td>&mdash;</td>
          <td>Maximum total results per category.</td>
        </tr>
        <tr>
          <td><code>full</code></td>
          <td>boolean</td>
          <td>true</td>
          <td>Include full entity metadata in results. Note: <code>entity</code> is present on
            results even when <code>full=false</code>.</td>
        </tr>
      </tbody>
    </table>

    <h3>Evidence Chains</h3>
    <p>
      For any search result, request the multi-hop evidence chain showing <em>how</em> your query
      entity connects to that result through intermediate nodes:
    </p>
    <CodeBlock
      title="Fetch evidence chains"
      examples={{
        python: `# After running a search and picking a target result:
target_id = "entrezgene:1956"  # EGFR

resp = requests.post(
    "/api/search",
    headers={"x-api-key": token, "Content-Type": "application/json"},
    json={
        "ids": ["unichem:161671"],
        "target_id": target_id,
    }
)
chains = resp.json()
for chain in chains.get("paths", []):
    path_str = " -> ".join(node["entity"]["title"] for node in chain)
    print(f"  Path: {path_str}")`,
        javascript: `const targetId = "entrezgene:1956"; // EGFR

const resp = await fetch("/api/search", {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    ids: ["unichem:161671"],
    target_id: targetId,
  })
});
const chains = await resp.json();
chains.paths?.forEach(chain => {
  const path = chain.map(n => n.entity.title).join(" -> ");
  console.log(\`  Path: \${path}\`);
});`,
        curl: `curl -s /api/search \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ids": ["unichem:161671"],
    "target_id": "entrezgene:1956"
  }' | jq '.paths[] | [.[].entity.title] | join(" -> ")'`,
      }}
    />

    <h3>Molecular Search</h3>
    <p>
      Match a SMILES string against known compounds (<code>GET /api/molecule/match</code>, returns
      an SVG), or compare two molecules by SMILES (<code>POST /api/molecule/compare</code>):
    </p>
    <CodeBlock
      title="Molecular structure matching"
      examples={{
        python: `# Match a SMILES string to known compounds (SVG rendering, mol vs query)
ASPIRIN = "CC(=O)Oc1ccccc1C(=O)O"
resp = requests.get(
    "/api/molecule/match",
    headers={"x-api-key": token},
    params={"mol": ASPIRIN, "query": ASPIRIN}
)
with open("match.svg", "wb") as f:
    f.write(resp.content)

# Compare two molecules by SMILES
IBUPROFEN = "CC(C)Cc1ccc(cc1)C(C)C(=O)O"
resp = requests.post(
    "/api/molecule/compare",
    headers={"x-api-key": token, "Content-Type": "application/json"},
    json={"smiles1": ASPIRIN, "smiles2": IBUPROFEN}
)
comparison = resp.json()
print(f"Similarity: {comparison.get('similarity', 'N/A')}")`,
        curl: `# Match SMILES to compounds (GET, query params, returns SVG)
ASPIRIN="CC(=O)Oc1ccccc1C(=O)O"
curl -s -G /api/molecule/match \\
  -H "x-api-key: $PLEX_TOKEN" \\
  --data-urlencode "mol=$ASPIRIN" \\
  --data-urlencode "query=$ASPIRIN" \\
  -o match.svg

# Compare two molecules by SMILES
curl -s /api/molecule/compare \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"smiles1": "CC(=O)Oc1ccccc1C(=O)O", "smiles2": "CC(C)Cc1ccc(cc1)C(C)C(=O)O"}' | jq .`,
        javascript: `// Match SMILES to compounds (GET, query params, returns SVG)
const aspirin = "CC(=O)Oc1ccccc1C(=O)O";
const params = new URLSearchParams({ mol: aspirin, query: aspirin });
const svg = await fetch(\`/api/molecule/match?\${params}\`, {
  headers: { "x-api-key": token }
}).then(r => r.text());

// Compare two molecules by SMILES
const ibuprofen = "CC(C)Cc1ccc(cc1)C(C)C(=O)O";
const comparison = await fetch("/api/molecule/compare", {
  method: "POST",
  headers: {
    "x-api-key": token,
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ smiles1: aspirin, smiles2: ibuprofen })
}).then(r => r.json());
console.log("Similarity:", comparison.similarity);`,
      }}
    />

    <h3>LLM Chat Integration</h3>
    <p>
      Send conversation messages with your search to get AI-powered analysis. The
      response streams via SSE with the same event protocol as search streaming:
    </p>
    <CodeBlock
      title="Chat with search context"
      examples={{
        python: `import json, requests
from sseclient import SSEClient  # use the bundled sseclient.py

HEADERS = {"x-api-key": token, "content-type": "application/json"}

# Send a chat message with search context (stream=true as query param)
resp = requests.post(
    "/api/search",
    params={"stream": True},
    headers=HEADERS,
    json={
        "ids": ["unichem:161671"],
        "messages": [{"role": "user",
                      "content": [{"text": "What are the main therapeutic targets of aspirin?"}]}]
    }
)
stream_id = resp.json()["stream_id"]
stream_url = f"/api/stream/{stream_id}"

# Stream the LLM response
sse_resp = requests.get(stream_url,
                        headers={**HEADERS, "Accept": "text/event-stream"},
                        stream=True)
answer = ""
for event in SSEClient(sse_resp, events=["*"], reconnect_url=stream_url).events():
    if event.event == "content_block_delta":
        text = json.loads(event.data).get("delta", {}).get("text", "")
        answer += text
        print(text, end="", flush=True)
    elif event.event == "message_stop":
        break
print()`,
        javascript: `// Send chat with search context (stream=true as query param)
const resp = await fetch("/api/search?stream=true", {
  method: "POST",
  headers: { "x-api-key": token, "Content-Type": "application/json" },
  body: JSON.stringify({
    ids: ["unichem:161671"],
    messages: [{
      role: "user",
      content: [{ text: "What are the main therapeutic targets of aspirin?" }]
    }]
  })
});
const { stream_id } = await resp.json();

// Connect to SSE stream
const es = new EventSource(\`/api/stream/\${stream_id}?token=\${token}\`);

let answer = "";
es.addEventListener("content_block_delta", (e) => {
  const text = JSON.parse(e.data)?.delta?.text || "";
  answer += text;
  process.stdout.write(text);
});

es.addEventListener("message_stop", () => {
  es.close();
  console.log("\\n--- Done ---");
});`,
        curl: `# Start chat stream
STREAM_ID=$(curl -s "/api/search?stream=true" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "ids": ["unichem:161671"],
    "messages": [{"role": "user", "content": [{"text": "What are the main targets of aspirin?"}]}]
  }' | jq -r '.stream_id')

# Stream the response
curl -N "/api/stream/$STREAM_ID" \\
  -H "x-api-key: $PLEX_TOKEN" \\
  -H "Accept: text/event-stream"`,
      }}
    />

  </section>
);

export default AdvancedTutorial;
